import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IIntegrationUIConfig, IIntegrationUIFieldOption } from '@feathr/components';
import { Button, CardV2, EmptyState, FieldMappingTable, Skeleton } from '@feathr/components';
import { useFeathrFieldOptions } from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPageV2/RaisersEdgeMappingsTab/RaisersEdgeMappingsTab.hooks';
import { useLocalUrl } from '@feathr/extender/state';

import type { ICrmMappingDraft, ICrmMappingStore } from '../crmMappings.hooks';
import type { ICrmContactMapping } from '../crmMappings.utils';

interface IProps<TMapping extends ICrmContactMapping> {
  /** The integration's mapping store, loaded by the caller. */
  store: ICrmMappingStore<TMapping>;
  config: IIntegrationUIConfig;
  draft: ICrmMappingDraft;
  crmIcon: ReactNode;
  /** The table's `data-name`; each row gets `<name>-<CRM field key>`. */
  name: string;
  /** The `data-name` of the load error state. */
  errorName: string;
  /** Rewrites the Feathr field options, e.g. to the ids the CRM's mappings name them by. */
  mapFeathrFieldOptions?: (
    options: readonly IIntegrationUIFieldOption[],
  ) => readonly IIntegrationUIFieldOption[];
}

function CrmMappingTable<TMapping extends ICrmContactMapping>({
  config,
  crmIcon,
  draft,
  errorName,
  mapFeathrFieldOptions,
  name,
  store,
}: Readonly<IProps<TMapping>>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const loadedOptions = useFeathrFieldOptions(true);
  const feathrFieldOptions =
    loadedOptions && mapFeathrFieldOptions ? mapFeathrFieldOptions(loadedOptions) : loadedOptions;

  function handleRetry(): void {
    void store.fetch();
  }

  if (!store.isFetched && store.error && !store.isPending) {
    return (
      <EmptyState
        description={t('Try again. If the problem continues, reload the page.')}
        label={t("Couldn't load mappings")}
        name={errorName}
      >
        <Button onClick={handleRetry} type={'primary'}>
          {t('Try again')}
        </Button>
      </EmptyState>
    );
  }

  if (!store.isFetched || !feathrFieldOptions) {
    return (
      <CardV2 width={'full'}>
        <CardV2.Content>
          <Skeleton paragraphs={4} title={true} />
        </CardV2.Content>
      </CardV2>
    );
  }

  return (
    <FieldMappingTable
      config={config}
      crmIcon={crmIcon}
      descriptionLink={{
        label: t('Add custom fields'),
        href: localUrl('/data/custom-fields/add'),
      }}
      feathrFieldOptions={feathrFieldOptions}
      mappings={draft.rows}
      name={name}
      onChange={draft.setRows}
    />
  );
}

export default observer(CrmMappingTable);
